/**
 * Connection Validation Types
 *
 * Types for connection validation, error handling, and status display.
 * Designed to be manifest-driven and support multi-slot connections.
 */

import type { ConnectionType, ConnectionReferenceKeyFormat } from './connectionReference';

// =============================================================================
// CONNECTION ERROR CODES
// =============================================================================

export const ConnectionErrorCode = {
  /** No connection assigned to required slot */
  MissingConnection: 'MISSING_CONNECTION',
  /** Reference key not found in connections.json */
  ReferenceNotFound: 'REFERENCE_NOT_FOUND',
  /** Connection resource doesn't exist or was deleted */
  ConnectionNotFound: 'CONNECTION_NOT_FOUND',
  /** Authentication token expired */
  ConnectionExpired: 'CONNECTION_EXPIRED',
  /** Authentication failed */
  ConnectionUnauthorized: 'CONNECTION_UNAUTHORIZED',
  /** General connection error */
  ConnectionError: 'CONNECTION_ERROR',
  /** Missing required configuration fields */
  InvalidConfiguration: 'INVALID_CONFIGURATION',
  /** Connection type mismatch */
  TypeMismatch: 'TYPE_MISMATCH',
} as const;
export type ConnectionErrorCode = (typeof ConnectionErrorCode)[keyof typeof ConnectionErrorCode];

// =============================================================================
// CONNECTION STATUS
// =============================================================================

export const ConnectionValidationStatus = {
  /** Connection is valid and working */
  Connected: 'connected',
  /** Connection has an error */
  Error: 'error',
  /** Connection has a warning (degraded but functional) */
  Warning: 'warning',
  /** Required connection is missing */
  Missing: 'missing',
  /** Connection status unknown (not yet validated) */
  Unknown: 'unknown',
  /** Connection is mocked (template mode with mock enabled) */
  MockActive: 'mock-active',
} as const;
export type ConnectionValidationStatus =
  (typeof ConnectionValidationStatus)[keyof typeof ConnectionValidationStatus];

// =============================================================================
// VALIDATION RESULT
// =============================================================================

/**
 * Error details for connection validation
 */
export interface ConnectionError {
  /** Error code for programmatic handling */
  code: ConnectionErrorCode;
  /** Human-readable error message */
  message: string;
  /** Whether the user can retry the connection */
  canRetry: boolean;
  /** Whether the user can reauthorize to fix the issue */
  canReauthorize: boolean;
}

/**
 * Result of validating a connection slot
 */
export interface ConnectionValidationResult {
  /** Whether the connection is valid */
  isValid: boolean;
  /** Connection status for UI display */
  status: ConnectionValidationStatus;
  /** Error details if validation failed */
  error?: ConnectionError;
}

// =============================================================================
// CONNECTION SLOT DEFINITIONS
// =============================================================================

/**
 * Connection slot definition from manifest
 * Enables operations to have multiple named connection requirements (e.g., Agent)
 */
export interface ConnectionSlotDefinition {
  /** Unique slot identifier (e.g., 'default', 'model1', 'model2') */
  slotId: string;
  /** Display name for UI */
  displayName: string;
  /** Whether this slot's connection is required */
  required: boolean;
  /** Allowed connection types for this slot */
  allowedTypes: ConnectionType[];
  /** Path in inputs where reference is stored */
  referencePath: string[];
  /** Description for UI */
  description?: string;
}

/**
 * Connection metadata extracted from operation manifest
 */
export interface ManifestConnectionMetadata {
  /** Whether any connection is required for this operation */
  required: boolean;
  /** Primary connection type */
  type: ConnectionType;
  /** Reference key format for serialization */
  referenceKeyFormat: ConnectionReferenceKeyFormat;
  /** Connection slots (single for most operations, multiple for Agent) */
  slots: ConnectionSlotDefinition[];
  /** Whether to disable auto-selection of connections */
  disableAutoSelection?: boolean;
}

// =============================================================================
// NODE CONNECTION STATE
// =============================================================================

/**
 * Connection state for a single node (supports multiple slots)
 */
export interface NodeConnectionState {
  /** Node ID */
  nodeId: string;
  /** Operation type (e.g., 'Http', 'Agent', 'ServiceBus') */
  operationType: string;
  /** Connection metadata from manifest (null if no connection needed) */
  manifest: ManifestConnectionMetadata | null;
  /** Slot assignments: slotId -> referenceKey */
  slots: Record<string, string | null>;
  /** Validation status per slot */
  validation: Record<string, ConnectionValidationResult>;
  /** Whether connection state is being loaded */
  isLoading: boolean;
}

// =============================================================================
// CONNECTION PATTERNS
// =============================================================================

/**
 * Standard connection reference patterns by connection type
 * Used for serialization/deserialization
 */
export const CONNECTION_PATTERNS: Record<
  ConnectionType,
  {
    /** Path in inputs where connection reference is stored */
    referencePath: string[];
    /** Field name containing the reference key */
    referenceKeyField: string;
  }
> = {
  apiconnection: {
    referencePath: ['inputs', 'host', 'connection'],
    referenceKeyField: 'referenceName',
  },
  serviceprovider: {
    referencePath: ['inputs', 'serviceProviderConfiguration'],
    referenceKeyField: 'connectionName',
  },
  agent: {
    referencePath: ['inputs', 'modelConfigurations'],
    referenceKeyField: 'referenceName',
  },
  mcp: {
    referencePath: ['inputs', 'mcpConfiguration'],
    referenceKeyField: 'connectionName',
  },
  function: {
    referencePath: ['inputs', 'function'],
    referenceKeyField: 'connectionName',
  },
  apimanagement: {
    referencePath: ['inputs', 'host', 'connection'],
    referenceKeyField: 'referenceName',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Create a validation result for a valid connection
 */
export function createValidResult(): ConnectionValidationResult {
  return {
    isValid: true,
    status: ConnectionValidationStatus.Connected,
  };
}

/**
 * Create a validation result for a missing connection
 */
export function createMissingResult(slotDisplayName: string): ConnectionValidationResult {
  return {
    isValid: false,
    status: ConnectionValidationStatus.Missing,
    error: {
      code: ConnectionErrorCode.MissingConnection,
      message: `${slotDisplayName} is required but not configured`,
      canRetry: false,
      canReauthorize: false,
    },
  };
}

/**
 * Create a validation result for a reference not found error
 */
export function createReferenceNotFoundResult(referenceKey: string): ConnectionValidationResult {
  return {
    isValid: false,
    status: ConnectionValidationStatus.Error,
    error: {
      code: ConnectionErrorCode.ReferenceNotFound,
      message: `Connection reference '${referenceKey}' not found`,
      canRetry: false,
      canReauthorize: false,
    },
  };
}

/**
 * Create a validation result for a warning (e.g. missing but not yet an error)
 */
export function createWarningResult(message: string): ConnectionValidationResult {
  return {
    isValid: false,
    status: ConnectionValidationStatus.Warning,
    error: {
      code: ConnectionErrorCode.MissingConnection,
      message,
      canRetry: false,
      canReauthorize: false,
    },
  };
}

/**
 * Create a validation result for a missing connection error
 */
export function createMissingConnectionError(message: string): ConnectionValidationResult {
  return {
    isValid: false,
    status: ConnectionValidationStatus.Error,
    error: {
      code: ConnectionErrorCode.MissingConnection,
      message,
      canRetry: false,
      canReauthorize: false,
    },
  };
}

/**
 * Create a validation result for a mock-active connection
 */
export function createMockActiveResult(): ConnectionValidationResult {
  return {
    isValid: true,
    status: ConnectionValidationStatus.MockActive,
  };
}

/**
 * Create a validation result for a general error
 */
export function createErrorResult(message: string, canRetry = true): ConnectionValidationResult {
  return {
    isValid: false,
    status: ConnectionValidationStatus.Error,
    error: {
      code: ConnectionErrorCode.ConnectionError,
      message,
      canRetry,
      canReauthorize: false,
    },
  };
}

/**
 * Get the most severe validation result from a set of slot validations
 * Used to determine the overall node connection status
 */
export function getMostSevereValidation(
  validations: Record<string, ConnectionValidationResult>
): ConnectionValidationResult | null {
  const results = Object.values(validations);
  if (results.length === 0) return null;

  // Find invalid results
  const invalid = results.filter((r) => !r.isValid);
  if (invalid.length === 0) {
    // All valid, return first (guaranteed to exist since results.length > 0)
    const first = results[0];
    return first ?? null;
  }

  // Sort by severity: missing > error > warning > unknown > mock-active > connected
  const severityOrder: Record<ConnectionValidationStatus, number> = {
    [ConnectionValidationStatus.Missing]: 0,
    [ConnectionValidationStatus.Error]: 1,
    [ConnectionValidationStatus.Warning]: 2,
    [ConnectionValidationStatus.Unknown]: 3,
    [ConnectionValidationStatus.MockActive]: 4,
    [ConnectionValidationStatus.Connected]: 5,
  };

  const sorted = [...invalid].sort(
    (a, b) => severityOrder[a.status] - severityOrder[b.status]
  );

  // Return first sorted result (guaranteed to exist since invalid.length > 0)
  const first = sorted[0];
  return first ?? null;
}

/**
 * Operation types that require a managed connection.
 * Uses an allowlist approach (aligned with LAUX manifest pattern):
 * only known connection-requiring types return true.
 * Unknown types default to NOT requiring connections — this prevents
 * false-positive "Connection not configured" errors on new or custom
 * action types.
 *
 * Keep in sync with OPERATION_TYPE_TO_CONNECTION_TYPE in manifestConnectionService.ts
 */
const CONNECTION_REQUIRED_OPERATIONS = new Set([
  // API connections (managed connectors)
  'ApiConnection',
  'ApiConnectionWebhook',
  'ApiConnectionNotification',

  // Service providers
  'ServiceProvider',

  // Agent operations
  'Agent',
  'AgentLoop',

  // MCP operations
  'Mcp',
  'McpTool',
]);

/**
 * Check if an operation type requires a connection.
 * Returns false for empty/undefined types and unknown operation types.
 */
export function operationRequiresConnection(operationType: string): boolean {
  if (!operationType) return false;
  return CONNECTION_REQUIRED_OPERATIONS.has(operationType);
}
